import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { fetchDashboardOverview, type DashboardOverviewParams } from '../api/dashboard-api.js';
import { AdminApiError } from '../api/admin-client.js';

export function dashboardOverviewQueryKey(params: DashboardOverviewParams) {
  return ['admin', 'dashboard', 'overview', params.preset, params.from ?? null, params.to ?? null] as const;
}

/**
 * Request cancellation: TanStack Query passes its own `AbortSignal` into
 * `queryFn` and aborts it automatically on unmount/query-key change/a
 * newer request superseding an in-flight one -- `fetchDashboardOverview`
 * forwards it straight to `fetch`, so a stale in-flight request is
 * actually cancelled at the network level, not just ignored client-side.
 */
export function useDashboardOverview(params: DashboardOverviewParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  return useQuery({
    queryKey: dashboardOverviewQueryKey(params),
    queryFn: async ({ signal }) => {
      const token = await getToken();
      if (!token) {
        throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
      }
      return fetchDashboardOverview(token, params, signal);
    },
    enabled: isLoaded && isSignedIn === true,
    staleTime: 30_000,
    // A 401/403/404/422 will never succeed on retry (the request itself is
    // the problem, not a transient failure) -- only genuinely transient
    // classes (network errors, 429, 500) get a couple of retries.
    retry: (failureCount, error) => {
      if (error instanceof AdminApiError && [401, 403, 404, 422].includes(error.status)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
